// Form-generator field-type → component map.
//
// Per specs/_product/field-primitive-registry.md §3. The form generator imports
// fieldRegistry and resolves fieldRegistry[spec.field_type] per field. Unknown
// field_type is a generation-time error.

import type { ComponentType } from "react";
import {
  EmailField,
  PhoneField,
  DateField,
  DateTimeField,
  CurrencyPicker,
  CountryPicker,
  AmountField,
  LocalePicker,
  TimezonePicker,
  DesignationPicker,
  DonorPicker,
  DonationTargetPicker,
  SchedulePicker,
  TagInput,
  ColorField,
  RichTextField,
  FileUploadField,
  TextField,
  TextareaField,
  SelectField,
  NumberField,
  CheckboxField,
  ToggleField,
  UrlField,
  RrulePicker,
  AssetPicker,
} from "./index";

// Use ComponentType<any> intentionally — primitive components have heterogeneous
// value-type generics; the form generator type-checks each field assignment via
// the FormSpec field schema, not at registry-lookup time.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fieldRegistry: Record<string, ComponentType<any>> = {
  email: EmailField,
  phone: PhoneField,
  date: DateField,
  datetime: DateTimeField,
  currency_picker: CurrencyPicker,
  country_picker: CountryPicker,
  amount: AmountField,
  locale_picker: LocalePicker,
  timezone_picker: TimezonePicker,
  designation_picker: DesignationPicker,
  donor_picker: DonorPicker,
  donation_target_picker: DonationTargetPicker,
  schedule_picker: SchedulePicker,
  tag_input: TagInput,
  color: ColorField,
  rich_text: RichTextField,
  file_upload: FileUploadField,
  // comp-ε GAP-3b-i (CHG-2433) — canonical basic primitives.
  text: TextField,
  textarea: TextareaField,
  select: SelectField,
  number: NumberField,
  checkbox: CheckboxField,
  // comp-ε GAP-3b-ii-a (CHG-2449) — accessible on/off switch (dominant
  // boolean field_type; distinct from `checkbox`, role="switch").
  toggle: ToggleField,
  // DRIFT-910 slice 1 (CHG-2457) — single-line URL input (native URL keyboard /
  // format hinting); a TextField sibling. `date` / `datetime` are promoted off
  // the CHG-1167 stub onto the real a11y pattern in the same slice (their
  // registry keys above are unchanged).
  url: UrlField,
  // DRIFT-910 slice 3 (CHG-2478) — RFC-5545 recurrence-cadence builder
  // (string-emitting); ported from the v1 picker, reimplemented on the v2
  // FieldShell/FieldPrimitiveProps contract.
  "rrule-picker": RrulePicker,
  // DRIFT-910 slice 4 (CHG-2483) — single-select asset picker over
  // assetsApi.listAssets (returns Asset.id) with a thumbnail preview.
  "asset-picker": AssetPicker,
};

export type FieldType = keyof typeof fieldRegistry;
